/**
 * ISO 3166-2 subdivisions for the countries whose shipping address needs a
 * state/province code (US, AU, CA, BR, JP — Printful's state lists). Mirrors the backend
 * `shared/static/iso-subdivisions.ts` — keep in sync.
 */
export interface IsoSubdivision {
  code: string;
  name: string;
}

export const STATE_REQUIRED_COUNTRIES = ['US', 'AU', 'CA', 'BR', 'JP'] as const;
export type StateRequiredCountry = (typeof STATE_REQUIRED_COUNTRIES)[number];

export const SUBDIVISIONS: Record<StateRequiredCountry, IsoSubdivision[]> = {
  US: [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'GU', name: 'Guam' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'VI', name: 'U.S. Virgin Islands' },
    { code: 'FM', name: 'Micronesia' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'PW', name: 'Palau' },
    { code: 'AA', name: 'Armed Forces Americas' },
    { code: 'AE', name: 'Armed Forces Europe' },
    { code: 'AP', name: 'Armed Forces Pacific' },
  ],
  AU: [
    { code: 'ACT', name: 'Australian Capital Territory' },
    { code: 'NSW', name: 'New South Wales' },
    { code: 'NT', name: 'Northern Territory' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'SA', name: 'South Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'WA', name: 'Western Australia' },
  ],
  CA: [
    { code: 'AB', name: 'Alberta' },
    { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NS', name: 'Nova Scotia' },
    { code: 'NT', name: 'Northwest Territories' },
    { code: 'NU', name: 'Nunavut' },
    { code: 'ON', name: 'Ontario' },
    { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' },
    { code: 'SK', name: 'Saskatchewan' },
    { code: 'YT', name: 'Yukon' },
  ],
  BR: [
    { code: 'AC', name: 'Acre' },
    { code: 'AL', name: 'Alagoas' },
    { code: 'AM', name: 'Amazonas' },
    { code: 'AP', name: 'Amapá' },
    { code: 'BA', name: 'Bahia' },
    { code: 'CE', name: 'Ceará' },
    { code: 'DF', name: 'Distrito Federal' },
    { code: 'ES', name: 'Espírito Santo' },
    { code: 'GO', name: 'Goiás' },
    { code: 'MA', name: 'Maranhão' },
    { code: 'MG', name: 'Minas Gerais' },
    { code: 'MS', name: 'Mato Grosso do Sul' },
    { code: 'MT', name: 'Mato Grosso' },
    { code: 'PA', name: 'Pará' },
    { code: 'PB', name: 'Paraíba' },
    { code: 'PE', name: 'Pernambuco' },
    { code: 'PI', name: 'Piauí' },
    { code: 'PR', name: 'Paraná' },
    { code: 'RJ', name: 'Rio de Janeiro' },
    { code: 'RN', name: 'Rio Grande do Norte' },
    { code: 'RO', name: 'Rondônia' },
    { code: 'RR', name: 'Roraima' },
    { code: 'RS', name: 'Rio Grande do Sul' },
    { code: 'SC', name: 'Santa Catarina' },
    { code: 'SE', name: 'Sergipe' },
    { code: 'SP', name: 'São Paulo' },
    { code: 'TO', name: 'Tocantins' },
  ],
  JP: [
    { code: '01', name: 'Hokkaido' },
    { code: '02', name: 'Aomori' },
    { code: '03', name: 'Iwate' },
    { code: '04', name: 'Miyagi' },
    { code: '05', name: 'Akita' },
    { code: '06', name: 'Yamagata' },
    { code: '07', name: 'Fukushima' },
    { code: '08', name: 'Ibaraki' },
    { code: '09', name: 'Tochigi' },
    { code: '10', name: 'Gunma' },
    { code: '11', name: 'Saitama' },
    { code: '12', name: 'Chiba' },
    { code: '13', name: 'Tokyo' },
    { code: '14', name: 'Kanagawa' },
    { code: '15', name: 'Niigata' },
    { code: '16', name: 'Toyama' },
    { code: '17', name: 'Ishikawa' },
    { code: '18', name: 'Fukui' },
    { code: '19', name: 'Yamanashi' },
    { code: '20', name: 'Nagano' },
    { code: '21', name: 'Gifu' },
    { code: '22', name: 'Shizuoka' },
    { code: '23', name: 'Aichi' },
    { code: '24', name: 'Mie' },
    { code: '25', name: 'Shiga' },
    { code: '26', name: 'Kyoto' },
    { code: '27', name: 'Osaka' },
    { code: '28', name: 'Hyogo' },
    { code: '29', name: 'Nara' },
    { code: '30', name: 'Wakayama' },
    { code: '31', name: 'Tottori' },
    { code: '32', name: 'Shimane' },
    { code: '33', name: 'Okayama' },
    { code: '34', name: 'Hiroshima' },
    { code: '35', name: 'Yamaguchi' },
    { code: '36', name: 'Tokushima' },
    { code: '37', name: 'Kagawa' },
    { code: '38', name: 'Ehime' },
    { code: '39', name: 'Kochi' },
    { code: '40', name: 'Fukuoka' },
    { code: '41', name: 'Saga' },
    { code: '42', name: 'Nagasaki' },
    { code: '43', name: 'Kumamoto' },
    { code: '44', name: 'Oita' },
    { code: '45', name: 'Miyazaki' },
    { code: '46', name: 'Kagoshima' },
    { code: '47', name: 'Okinawa' },
  ],
};

export function requiresState(
  countryCode: string | null | undefined
): countryCode is StateRequiredCountry {
  return (STATE_REQUIRED_COUNTRIES as readonly string[]).includes(countryCode ?? '');
}
